// eslint-disable-next-line max-classes-per-file
import get from 'lodash.get';
import _ from 'underscore';
import { ID } from 'app/blocks/common/codes';
import ContentUtils from 'app/blocks/common/content-utils';
import Emitter from 'app/blocks/common/events/emitter';
import Events from 'app/blocks/common/events/Events';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import Alerts from 'app/blocks/common/spinner';
import { cleanToken, Deferred } from 'app/blocks/common/utils';
import { getUser, logout } from 'app/blocks/middleware/authentication';
import * as OrcidMiddleware from 'app/blocks/middleware/orcid';
import * as ProfileMiddleware from 'app/blocks/middleware/profile';
import * as RegisterMiddleware from 'app/blocks/middleware/register';
import { confirmAffiliationImport, confirmLogin, notifyUserThatOrcidImportingFailed } from 'app/blocks/orcid/utils';
import { saveOrcidProfileInformation } from 'app/pages/profile/profile-information/utils';
import { importAffiliations } from './OrcidAffiliationsImporter';
import OrcidDataValidator from './OrcidDataValidator';

class OrcidProfileProcessor {
    // eslint-disable-next-line consistent-return
    static async updateWithOrcid(orcidData, shouldBeUpdated, orcidCode) {
        let result;
        const data = orcidData.user || orcidData;

        try {
            Alerts.renderSpinner();
            const profileInformation = await ProfileMiddleware.getProfileInformation();
            const newProfileInformation = await OrcidProfileProcessor.saveProfile(
                data,
                shouldBeUpdated,
                profileInformation,
            );
            Alerts.removeSpinner();

            result = { orcidId: newProfileInformation.orcidId };

            if (orcidData.affiliations && orcidData.affiliations.length > 0) {
                result.affiliations = orcidData.affiliations;
                importAffiliations(orcidCode, orcidData.affiliations);
            }

            Emitter.emit(Events.PROFILE_INFORMATION_IMPORTED, {
                firstName: newProfileInformation.firstName,
                lastName: newProfileInformation.lastName,
                primaryEmailAddr: newProfileInformation.primaryEmailAddr,
                countryCode: newProfileInformation.countryCode,
                countryNameNE: newProfileInformation.countryNameNE,
                orcidId: newProfileInformation.orcidId,
            });
        } catch (error) {
            Alerts.removeSpinner();
            if (error.code === 'PROFILE_UPDATE_ORCID_ID_CONFLICT') {
                // eslint-disable-next-line no-use-before-define
                await OrcidProfileProcessor.orcidExistsDialog(error);
            } else {
                showDialog.error(error, {
                    message: await ContentUtils.getServerErrorMessage(ID.PROFILE_ACCOUNT, error, error.payload),
                });
            }
        }
    }

    static async orcidExistsDialog(response) {
        if (await confirmLogin(response.payload)) {
            logout();
        }
    }

    static async saveProfile(data, shouldBeUpdated, profileInformation) {
        const newProfileInformation = { ...profileInformation, orcidId: data.orcidId };
        if (shouldBeUpdated) {
            const country = get(data, 'addresses.correspondenceAddress.country');
            newProfileInformation.firstName = data.firstName || profileInformation.firstName;
            newProfileInformation.lastName = data.lastName || profileInformation.lastName;
            newProfileInformation.primaryEmailAddr = data.primaryEmailAddr || profileInformation.primaryEmailAddr;

            if (country && country.countryCode) {
                newProfileInformation.countryCode = country.countryCode;
                newProfileInformation.countryNameNE = country.countryName;
            }
        }

        const fields = ['firstName', 'lastName', 'countryCode', 'orcidId'].map(k => [
            k,
            newProfileInformation[k] || '',
        ]);
        const info = Object.fromEntries(new Map(fields));
        Object.assign(newProfileInformation, info);
        await saveOrcidProfileInformation(info);
        return newProfileInformation;
    }

    static takeInfo(newProfileInformation) {
        return {
            firstName: newProfileInformation.firstName,
            lastName: newProfileInformation.lastName,
            primaryEmailAddr: newProfileInformation.primaryEmailAddr,
            countryCode: newProfileInformation.countryCode,
            countryNameNE: newProfileInformation.countryNameNE,
            orcidId: newProfileInformation.orcidId,
        };
    }
}

class OrcidProfileRegistrationProcessor {
    static updateWithOrcid = async (orcidData, shouldBeUpdated, orcidCode) => {
        const { user, affiliations } = orcidData;
        const options = {};
        let fields = {};

        if (user && user.orcidId) {
            options.orcidId = user.orcidId;
            options.shouldBeUpdated = shouldBeUpdated;

            if (shouldBeUpdated) {
                fields = {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    EmailAddr: user.primaryEmailAddr,
                    country: get(user, 'addresses.correspondenceAddress.country'),
                };
            }

            if (!_.isEmpty(affiliations)) {
                if (await confirmAffiliationImport(affiliations)) {
                    options.orcidCode = orcidCode;
                    options.complete = async token => {
                        if (orcidCode) {
                            await OrcidMiddleware.importAffiliationsInRegistration(orcidCode, cleanToken(token));
                        }
                    };

                    Emitter.emit(Events.ORCID_IMPORTED, fields, options);
                }
            } else {
                options.complete = () => Promise.resolve();
                Emitter.emit(Events.ORCID_IMPORTED, fields, options);
            }
        }
    };
}

class OrcidInfoImporter {
    static DATA_FIELDS = ['firstName', 'lastName', 'primaryEmailAddr', 'countryCode'];

    import(orcidCode, accessDenied) {
        const callback = async () => {
            if (orcidCode) {
                await this.importDataFromOrcid(orcidCode);
            }

            if (!accessDenied) {
                const error = await ContentUtils.getServerErrorMessage();
                showDialog.error({
                    message: error,
                });
            }
        };
        callback.acessDenied = accessDenied;

        return callback;
    }

    async importDataFromOrcid(orcidCode) {
        try {
            const { orcidData, errors } = await this.getOrcidData(orcidCode);
            const modifiedData = await this.getModifiedData(orcidData);
            const approved = await this.updateDataWithOrcid.bind(null, true, orcidData, orcidCode);
            const rejected = await this.updateDataWithOrcid.bind(null, false, orcidData, orcidCode);

            if (this.anyFieldModified(modifiedData)) {
                const data = { ...orcidData, user: modifiedData };
                this.askToAcceptModifications(data, errors, approved, rejected);
            } else if (errors.length) {
                await this.showErrors(errors, rejected);
            } else {
                rejected();
            }
        } catch (error) {
            if (error.code === 'REGISTRATION_ORCID_ALREADY_EXISTS') {
                await OrcidProfileProcessor.orcidExistsDialog(error);
            } else {
                showDialog.error(error);
            }
        }
    }

    async getOrcidData(orcidCode) {
        const orcidData = await OrcidMiddleware.getData(orcidCode, true);
        await RegisterMiddleware.verifyOrcid(orcidData.user.orcidId);
        const orcidDataValidation = await OrcidDataValidator.validateOrcidData(orcidData.user);

        if (!_.isEmpty(orcidDataValidation.errors)) {
            orcidData.user = orcidDataValidation.validData;
        }

        orcidData.user.countryCode = get(orcidData.user, 'addresses.correspondenceAddress.country.countryCode');

        return { orcidData, errors: orcidDataValidation.errors };
    }

    async getModifiedData(orcidData) {
        const profileInformation = getUser().loggedIn ? await ProfileMiddleware.getProfileInformation() : {};

        const entries = Object.entries(orcidData.user).filter(
            ([key, value]) =>
                (OrcidInfoImporter.DATA_FIELDS.includes(key) && value && value !== profileInformation[key]) ||
                !OrcidInfoImporter.DATA_FIELDS.includes(key),
        );

        const result = Object.fromEntries(entries);

        if (!result.countryCode) {
            delete result.addresses;
        }

        return result;
    }

    anyFieldModified(modifiedData) {
        return OrcidInfoImporter.DATA_FIELDS.some(key => key in modifiedData);
    }

    async askToAcceptModifications(data, errors, approved) {
        approved();
    }

    async showErrors(errors, onClose) {
        await notifyUserThatOrcidImportingFailed(errors, onClose);
    }

    async updateDataWithOrcid(updateData, data, orcidCode) {
        if (getUser().loggedIn) {
            await OrcidProfileProcessor.updateWithOrcid(data, updateData, orcidCode);
        } else {
            await OrcidProfileRegistrationProcessor.updateWithOrcid(data, updateData, orcidCode);
        }
    }

    static generateDataImportObject() {
        const dataImport = new Deferred();

        dataImport.completed = function (result) {
            this.result = result;
            this.resolve();
        };

        return dataImport;
    }
}

const TestKit = {
    OrcidInfoImporterClass: OrcidInfoImporter,
};
export { TestKit };

const importer = new OrcidInfoImporter();
export default importer.import.bind(importer);
